import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import type { SelectProps } from "antd";
import { Select, Tooltip } from "antd";
import { FC, useMemo, useState } from "react";
import { SortOrder } from "tm-shared/types";

import { StyledButton, StyledContainer } from "./Styled";

type Size = "small" | "middle" | "large";
const ICON_SIZE: Record<Size, number> = {
    small: 12,
    middle: 14,
    large: 16,
};

type SelectWithToggleProps = {
    options: SelectProps["options"];
    selectValue?: SelectProps["value"];
    onChangeSelect?: SelectProps["onChange"];
    /** controlled */
    order?: SortOrder;
    /** uncontrolled (default) */
    defaultOrder?: SortOrder;
    onOrderChange?: (order: SortOrder) => void;
    size?: Size;
    disabled?: boolean;
    minSelectWidth?: number | string;
    selectWidth?: number | string;
};

export const SelectWithToggle: FC<SelectWithToggleProps> = ({
    options,
    selectValue,
    onChangeSelect,
    order: orderProp, // controlled
    defaultOrder = SortOrder.Asc, // uncontrolled
    onOrderChange,
    size = "large",
    disabled,
    minSelectWidth = 160,
    selectWidth = 160,
}) => {
    const isControlled = orderProp !== undefined;
    const [innerOrder, setInnerOrder] = useState<SortOrder>(defaultOrder);

    const order = isControlled ? orderProp : innerOrder;

    const toggleOrder = () => {
        const next = order === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc;
        if (!isControlled) setInnerOrder(next);
        onOrderChange?.(next);
    };

    const tooltipTitle = useMemo(
        () => (order === SortOrder.Desc ? "За спаданням" : "За зростанням"),
        [order],
    );

    return (
        <StyledContainer>
            <Select
                style={{ minWidth: minSelectWidth, width: selectWidth }}
                options={options}
                value={selectValue}
                onChange={onChangeSelect}
                size={size}
                disabled={disabled}
                placeholder={"Сортувати за:"}
                allowClear
            />
            <Tooltip title={tooltipTitle}>
                <StyledButton
                    aria-label={tooltipTitle}
                    aria-pressed={order === SortOrder.Desc}
                    onClick={toggleOrder}
                    size={size}
                    disabled={disabled}
                >
                    {order === SortOrder.Asc ? (
                        <ArrowUpOutlined
                            style={{ fontSize: ICON_SIZE[size] }}
                        />
                    ) : (
                        <ArrowDownOutlined
                            style={{ fontSize: ICON_SIZE[size] }}
                        />
                    )}
                </StyledButton>
            </Tooltip>
        </StyledContainer>
    );
};
